// ═══════════════════════════════════════════════════════════════
// Weboldal-átemelés futtatója – a képernyőtől FÜGGETLENÜL fut
//
// Korábban az átemelés ciklusa a WebScraperView komponensben futott: menüváltáskor
// a komponens eltűnt, a felület elvesztette a fonalat, visszatérve nem folytatódott.
// Most a ciklus ebben a modulban él (az alkalmazás teljes élettartamán át):
//   - menüváltás alatt is fut, az állapotát a munkamenetbe (sessionStorage) írja;
//   - a képernyő visszatéréskor újra „rácsatlakozik” (attach);
//   - teljes oldalfrissítés után a képernyő a mentett import-azonosítóval folytatja.
// A szerver zárolja az importot (egyszerre egy lépés), így két futtató sem dolgozhat
// kétszer ugyanazon a tételen – ha foglalt, a futtató vár és újra próbálja.
// ═══════════════════════════════════════════════════════════════
import { ApiClient } from '../../api/client';

export const SCRAPER_STORAGE_KEY = 'kozadv2_scraper_session_state';

export interface RunnerHandlers {
  onLog: (msg: string) => void;
  onProgress: (p: { done: number; total: number }) => void;
  onWait: (sec: number) => void;
  onRunning: (running: boolean) => void;
  onCompleted: () => void;
  onPaused: (importId: number) => void;
}

// a munkamenet-állapot közvetlen frissítése (akkor is, ha a képernyő nincs megnyitva)
function patchSaved(fn: (s: any) => any) {
  try {
    const raw = sessionStorage.getItem(SCRAPER_STORAGE_KEY);
    const cur = raw ? JSON.parse(raw) : {};
    sessionStorage.setItem(SCRAPER_STORAGE_KEY, JSON.stringify(fn(cur)));
  } catch {
    // tárhelykorlát / privát mód: a képernyő a saját állapotából dolgozik tovább
  }
}

class ScrapeImportRunner {
  private handlers: RunnerHandlers | null = null;
  private runningId: number | null = null;
  private stopped = false;
  private waitSec = 0;

  attach(h: RunnerHandlers) { this.handlers = h; h.onRunning(this.runningId !== null); h.onWait(this.waitSec); }
  detach(h: RunnerHandlers) { if (this.handlers === h) this.handlers = null; }
  isRunning(id?: number | null) { return this.runningId !== null && (id == null || this.runningId === id); }
  stop() { this.stopped = true; }

  private log(msg: string) {
    patchSaved((s) => ({ ...s, logs: [...(s.logs || []), msg].slice(-200) }));
    this.handlers?.onLog(msg);
  }
  private progress(done: number, total: number) {
    patchSaved((s) => ({ ...s, importProgress: { done, total } }));
    this.handlers?.onProgress({ done, total });
  }
  private setWait(sec: number) { this.waitSec = sec; this.handlers?.onWait(sec); }

  /** Elindítja (vagy folytatja) az importot; ha már fut, nem indít még egyet. */
  async run(importId: number) {
    if (this.runningId !== null) return;
    this.runningId = importId;
    this.stopped = false;
    patchSaved((s) => ({ ...s, activeImportId: importId, pausedImportId: null }));
    this.handlers?.onRunning(true);
    let retries = 0;
    let busyNoted = false;
    let skipped = 0;
    try {
      while (!this.stopped) {
        const started = Date.now();
        this.setWait(0);
        const ticker = window.setInterval(() => this.setWait(Math.round((Date.now() - started) / 1000)), 1000);
        let res: any;
        try {
          res = await ApiClient.scrapeImportStep(importId);
        } finally {
          window.clearInterval(ticker);
          this.setWait(0);
        }
        if (this.stopped) break;

        if (!res.ok) {
          if (retries < 3) {
            retries++;
            this.log(`⚠️ Átmeneti hálózati lassulás (${res.error || ''}), újrapróbálkozás (${retries}/3)...`);
            await new Promise((r) => setTimeout(r, 2500));
            continue;
          }
          this.log(`❌ Megszakadt a kapcsolat: ${res.error}`);
          patchSaved((s) => ({ ...s, activeImportId: null, pausedImportId: importId }));
          this.handlers?.onPaused(importId);
          break;
        }
        retries = 0;

        if (res.busy) {
          // egy korábbi lépés még fut a szerveren (pl. időtúllépés utáni újrapróbálás) – kivárjuk
          if (!busyNoted) this.log('⏳ Az előző lépés még fut a szerveren, várakozás…');
          busyNoted = true;
          this.progress(res.progress_done, res.progress_total);
          await new Promise((r) => setTimeout(r, 4000));
          continue;
        }
        busyNoted = false;

        this.progress(res.progress_done, res.progress_total);
        for (const w of (res.warnings || [])) { skipped++; this.log(`⚠️ Kihagyott melléklet – ${w}`); }
        if (res.partial) {
          const cur = res.current ? `${res.current.pont || ''} ${res.current.cim || ''}`.trim() : '';
          this.log(`⏳ Sok vagy nagy melléklet${cur ? ` („${cur.slice(0, 60)}”)` : ''} – a letöltés folytatódik…`);
        } else {
          this.log(`📥 Átemelve: ${res.progress_done} / ${res.progress_total} rekord és melléklet`);
        }

        if (res.done) {
          this.log('✅ Minden adatlap és fájl sikeresen importálva és tárolva!');
          if (skipped > 0) {
            this.log(`ℹ️ ${skipped} melléklet nem töltődött le. A listájuk és az újrapróbálás: Adatlapok & Bejegyzések → Fájltípus statisztika → „HTML / Hiba”.`);
          }
          patchSaved((s) => ({ ...s, activeImportId: null, pausedImportId: null, importCompleted: true }));
          this.handlers?.onCompleted();
          break;
        }
      }
    } catch (e: any) {
      this.log(`❌ Hiba: ${e?.message || e}`);
      patchSaved((s) => ({ ...s, activeImportId: null, pausedImportId: importId }));
      this.handlers?.onPaused(importId);
    } finally {
      this.runningId = null;
      this.handlers?.onRunning(false);
    }
  }
}

export const scrapeImportRunner = new ScrapeImportRunner();

// ═══════════════════════════════════════════════════════════════
// Felfedezés (1. lépés) futtatója – szintén a képernyőtől függetlenül
//
// A felfedezés lépésenként halad (a szerver állapotmentes: a sor, a bejárt oldalak és a
// terv minden lépés után visszajön). Minden lépés után a munkamenetbe kerül egy
// ellenőrzőpont (discovery), így:
//   - menüváltás alatt a ciklus fut tovább, visszatéréskor a képernyő rácsatlakozik;
//   - teljes oldalfrissítés után az utolsó ellenőrzőponttól folytatódik (nem elölről).
// ═══════════════════════════════════════════════════════════════
export interface DiscoveryHandlers {
  onLog: (msg: string) => void;
  onProgress: (pct: number) => void;
  onRunning: (running: boolean) => void;
  onDone: (plan: any) => void;
  onFailed: (error: string) => void;
}

export interface DiscoveryCheckpoint {
  sourceUrls: string[];
  queue: any[];
  visited: any[];
  plan: any;
  stepCount: number;
}

const discoveryPct = (stepCount: number, visited: number, queued: number) => {
  // a sor hosszából becsült haladás, de lépésenként legalább valamennyit lép előre
  const byQueue = visited + queued > 0 ? Math.round((visited / (visited + queued)) * 100) : 0;
  return Math.min(95, Math.max(10 + stepCount * 5, byQueue));
};

class ScrapeDiscoveryRunner {
  private handlers: DiscoveryHandlers | null = null;
  private running = false;
  private stopped = false;
  private pct = 0;

  attach(h: DiscoveryHandlers) { this.handlers = h; h.onRunning(this.running); if (this.running) h.onProgress(this.pct); }
  detach(h: DiscoveryHandlers) { if (this.handlers === h) this.handlers = null; }
  isRunning() { return this.running; }
  stop() { this.stopped = true; }

  private log(msg: string) {
    patchSaved((s) => ({ ...s, logs: [...(s.logs || []), msg].slice(-200) }));
    this.handlers?.onLog(msg);
  }
  private progress(pct: number) {
    this.pct = pct;
    patchSaved((s) => ({ ...s, discoverProgress: pct }));
    this.handlers?.onProgress(pct);
  }

  /** Új felfedezés indítása, vagy – ha van ellenőrzőpont – folytatás onnan. */
  async run(sourceUrls: string[], resumeFrom?: DiscoveryCheckpoint | null) {
    if (this.running) return;
    this.running = true;
    this.stopped = false;
    this.handlers?.onRunning(true);
    patchSaved((s) => ({ ...s, discoveryRunning: true }));

    let stepCount = resumeFrom?.stepCount || 0;
    let reqBody: any = resumeFrom
      ? { queue: resumeFrom.queue, visited: resumeFrom.visited, plan: resumeFrom.plan }
      : { sourceUrls };
    if (resumeFrom) {
      this.log(`🔄 Felfedezés folytatása ott, ahol abbamaradt (${resumeFrom.visited?.length || 0} oldal kész, ${resumeFrom.queue?.length || 0} van hátra)...`);
    } else {
      patchSaved((s) => ({ ...s, logs: [], discovery: null, discoverProgress: 0 }));
      this.log(`🔍 Weboldal feltérképezése indul (${sourceUrls.length} forrás URL)...`);
      this.progress(0);
    }

    let retries = 0;
    try {
      while (!this.stopped) {
        const res: any = await ApiClient.scrapeDiscover(reqBody);
        if (this.stopped) break;
        if (!res.ok) {
          // hálózati megszakadás: rövid várakozás után ugyanazzal az ellenőrzőponttal újra
          if (retries < 3 && reqBody.queue) {
            retries++;
            this.log(`⚠️ Átmeneti hiba (${res.error || 'nincs válasz'}), újrapróbálkozás (${retries}/3)...`);
            await new Promise((r) => setTimeout(r, 2500));
            continue;
          }
          const msg = res.error || 'A weboldal nem érhető el';
          this.log(`❌ Hiba: ${msg}`);
          this.handlers?.onFailed(msg);
          break;
        }
        retries = 0;
        stepCount++;

        (res.warnings || []).forEach((w: string) => this.log(`⚠️ ${w}`));
        (res.fetchedThisStep || []).forEach((u: string) => this.log(`✓ Feltérképezve: ${u}`));

        if (res.done) {
          this.log(`🎉 Felfedezés kész! Összesen ${res.visited?.length || 0} oldal feldolgozva.`);
          this.progress(100);
          patchSaved((s) => ({ ...s, discovery: null, plan: res.plan, step: 2 }));
          this.handlers?.onDone(res.plan);
          break;
        }

        const cp: DiscoveryCheckpoint = { sourceUrls, queue: res.queue, visited: res.visited, plan: res.plan, stepCount };
        patchSaved((s) => ({ ...s, discovery: cp }));
        this.progress(discoveryPct(stepCount, res.visited?.length || 0, res.queue?.length || 0));
        reqBody = { queue: res.queue, visited: res.visited, plan: res.plan };
      }
    } catch (e: any) {
      const msg = e?.message || String(e);
      this.log(`❌ Hiba a hálózati kapcsolatban: ${msg}`);
      this.handlers?.onFailed(msg);
    } finally {
      this.running = false;
      patchSaved((s) => ({ ...s, discoveryRunning: false }));
      this.handlers?.onRunning(false);
    }
  }
}

export const scrapeDiscoveryRunner = new ScrapeDiscoveryRunner();
